import {
  base_colorAction,
  base_colorActionHover,
  base_colorContentBorder,
  base_contentBackground,
  cta_buttonSecondaryBackgroundHover,
  grey50,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
  font_lg,
  font_md,
  font_size_xs,
  font_sm,
  fontBold,
  fontFormLabel,
  fontH3,
  fontRegular,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const subtitle = css`
  ${font_md};
  color: ${text_colorTextSecondary};
`

export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export const wizardLayout = css`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${spacing(5)};
`

export const stepperCard = css`
  flex: 0 0 ${spacing(70)};
  padding: ${spacing(5)};
`

export const contentCard = css`
  flex: 1 1 auto;
  padding: ${spacing(6)} ${spacing(7)};
`

export const stepperTitle = css`
  font-family: ${font_family_weight_regular};
  ${font_lg};
  color: ${text_colorTextPrimary};
  margin: 0 0 ${spacing(4)} 0;
`

export const stepperList = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
`

export const stepperItem = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(3)};
  ${fontFormLabel};
  font-family: ${font_family_weight_semibold};

  &.active {
    color: ${text_colorTextPrimary};
  }

  &.active .step-label {
    color: ${base_colorAction};
  }

  &[data-state='completed'] .step-label {
    color: ${text_colorTextSecondary};
  }
`

export const stepBadgeBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${spacing(4)};
  height: ${spacing(4)};
  border-radius: 50%;
  font-family: ${font_family_weight_regular};
  font-size: ${font_size_xs};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

export const stepBadgeUpcoming = css`
  ${stepBadgeBase};
  background: ${base_contentBackground};
  color: ${text_colorTextSecondary};
  border: 1px solid ${base_colorContentBorder};
`

export const stepBadgeActive = css`
  ${stepBadgeBase};
  background: ${base_colorAction};
  color: ${base_contentBackground};
`

export const stepBadgeCompleted = css`
  ${stepBadgeBase};
  background: ${text_colorTextSecondary};
  color: ${base_contentBackground};

  svg {
    transform: translateY(1px);
  }
`

export const stepHeader = css`
  display: flex;
  flex-direction: column;
  ${fontRegular};
  gap: ${spacing(2)};
  margin-bottom: ${spacing(6)};
`

export const stepEyebrow = css`
  font-family: ${font_family_weight_semibold};
  ${font_sm};
  color: ${base_colorAction};
`

export const stepHeading = css`
  ${fontH3};
  color: ${text_colorTextPrimary};
  margin: 0;
`

export const stepBody = css`
  ${fontRegular};
  color: ${text_colorTextPrimary};
  margin: 0;
`

export const stepBodyEmphasis = css`
  font-family: ${font_family_weight_regular};
`

export const stepActions = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: ${spacing(8)};
`

export const stepActionsRight = css`
  display: flex;
  flex-direction: row;
  gap: ${spacing(3)};
  margin-left: auto;
`

export const networkGuideCard = css`
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  padding: ${spacing(5)} ${spacing(6)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  margin-bottom: ${spacing(4)};
`

export const networkGuideHeader = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(3)};
`

export const networkGuideTitle = css`
  ${fontH3};
  color: ${text_colorTextPrimary};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(3)};
  margin: 0;
`

export const networkGuideLogo = css`
  flex: 0 0 auto;
`

export const networkGuideBody = css`
  ${fontRegular};
  color: ${text_colorTextSecondary};
  margin: 0;
`

export const guideLinkButton = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
  padding: ${spacing(2)} ${spacing(4)};
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${spacing(2)};
  background: ${base_contentBackground};
  color: ${base_colorAction};
  font-family: ${font_family_weight_medium};
  ${font_md};
  line-height: 16px;
  text-decoration: none;
  white-space: nowrap;

  & svg path {
    fill: ${base_colorAction};
  }

  &:hover {
    background: ${cta_buttonSecondaryBackgroundHover};
    color: ${base_colorActionHover};

    & svg path {
      fill: ${base_colorActionHover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
  }
`

export const fieldGroup = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};
  margin-bottom: ${spacing(5)};
`

export const fieldLabel = css`
  ${fontFormLabel};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(1)};
`

// TODO: replace grey50 (base palette) with a semantic surface token once one
// is added to ext-ui — design intent is "muted card surface," not the raw hex.
export const termsBox = css`
  background: ${grey50};
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  padding: ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  margin-bottom: ${spacing(5)};
`

export const termsBoxIntro = css`
  ${fontBold};
  color: ${text_colorTextPrimary};
`

export const termsBoxList = css`
  ${fontRegular};
  color: ${text_colorTextSecondary};
  margin: 0;
  padding-left: ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
`

export const termsAcceptRow = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(3)};
  ${fontRegular};
  color: ${text_colorTextPrimary};
  cursor: pointer;
`

export const whatHappensList = css`
  ${fontRegular};
  ${font_md};
  color: ${text_colorTextSecondary};
  margin: 0;
  padding-left: ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
`
